import m from 'mithril';
import { Store } from '../store';
import { ApiService } from '../services/api';
import { t } from '../i18n';
import { Button } from './Buttons';

const InputGroup = (label: string, icon: string, type: string, placeholder: string, oninput: (val: string) => void, rightBtn?: m.Children) => 
    m('div', { class: 'space-y-1.5' }, [
        m('label', { class: 'text-xs font-black text-[var(--color-text-base)] opacity-70 tracking-widest uppercase ms-1 transition-all group-focus-within:text-[var(--color-primary)] group-focus-within:opacity-100' }, label),
        m('div', { class: 'relative group' }, [
            m('iconify-icon', { icon, class: 'absolute start-4 top-1/2 -translate-y-1/2 text-[var(--color-text-base)] flex opacity-40 group-focus-within:opacity-100 group-focus-within:text-[var(--color-primary)] transition-colors group-focus-within:drop-shadow-[0_0_8px_var(--color-primary-glow)]' }),
            m('input', {
                type, required: true, placeholder,
                class: `w-full ps-12 py-3.5 bg-[var(--color-surface)] border-2 border-[var(--color-border)] rounded-xl outline-none focus:border-[var(--color-primary)] focus:ring-4 focus:ring-[var(--color-primary-glow)] transition-all duration-300 font-medium text-[var(--color-text-base)] placeholder:text-[var(--color-text-base)] placeholder:opacity-30 shadow-sm hover:shadow-md hover:border-opacity-50 ${rightBtn ? 'pe-12' : 'pe-4'}`,
                oninput: (e: any) => oninput(e.target.value)
            }),
            rightBtn
        ])
    ]);

export const AuthModal: m.Component = {
    view() {
        if (!Store.state.isAuthModalOpen) return null;

        return m('div', { class: 'fixed inset-0 z-[100] flex items-center justify-center' }, [
            // Backdrop
            m('div', {
                class: 'absolute inset-0 bg-[var(--color-bg-base)]/60 backdrop-blur-xl transition-opacity animate-fade-in',
                onclick: () => { Store.state.isAuthModalOpen = false; }
            }),

            // Modal Container
            m('div', {
                class: 'bg-[var(--color-surface)]/80 backdrop-blur-3xl w-full max-w-md m-4 rounded-2xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] relative z-10 overflow-hidden animate-scale-in border border-[var(--color-border)]/50 ring-1 ring-white/20'
            }, [
                // Modal Header
                m('div', { class: 'relative h-40 bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-secondary)] overflow-hidden' }, [
                    // Abstract overlapping circles for depth
                    m('div', { class: 'absolute -top-10 -end-10 w-40 h-40 bg-white/10 rounded-full blur-2xl' }),
                    m('div', { class: 'absolute -bottom-10 -start-10 w-40 h-40 bg-black/10 rounded-full blur-2xl' }),
                    m('button', {
                        class: 'absolute top-4 end-4 text-white/80 hover:text-white transition-all transform hover:rotate-90 !p-2 !m-0 rounded-lg bg-black/10 hover:bg-black/20 backdrop-blur-md',
                        onclick: () => { Store.state.isAuthModalOpen = false; }
                    }, m('iconify-icon', { icon: 'lucide:x', class: 'text-xl' })),
                    m('div', { class: 'absolute bottom-8 start-8 text-white' }, [
                        m('div', { class: 'w-10 h-10 rounded-lg bg-white/20 backdrop-blur-md flex items-center justify-center mb-3 border border-white/20 shadow-inner' }, [
                            m('iconify-icon', { icon: state.isLogin ? 'lucide:fingerprint' : 'lucide:user-plus', class: 'text-xl drop-shadow-md' })
                        ]),
                        m('h2', { class: 'text-3xl font-black tracking-tight drop-shadow-md' }, state.isLogin ? 'Welcome Back' : 'Create Account'),
                        m('p', { class: 'text-white/90 text-sm mt-1 font-medium drop-shadow-sm' }, state.isLogin ? 'Sign in to access your dashboard' : 'Join our premium community today')
                    ])
                ]),

                // Auth Form
                m('form', {
                    class: 'p-8 space-y-5',
                    onsubmit: async (e: Event) => {
                        e.preventDefault();
                        state.loading = true;
                        state.error = null;

                        try {
                            if (state.isLogin) {
                                const result = await ApiService.login({ email: state.email, password: state.password });
                                if (!result || !result.success) throw new Error(result?.error || 'Invalid credentials');
                                Store.state.user = result.user;
                            } else {
                                const result = await ApiService.register({ name: state.name, email: state.email, password: state.password });
                                if (!result || !result.success) throw new Error(result?.error || 'Registration failed');
                                Store.state.user = result.user;
                            }
                            
                            // Load user data
                            const [favsRes, cartRes] = await Promise.all([
                                ApiService.getFavorites(),
                                ApiService.getCart()
                            ]);
                            if (favsRes?.success) Store.state.favorites = favsRes.data.map((f: any) => f.product);
                            if (cartRes?.success) Store.state.cart = cartRes.data;

                            Store.state.isAuthModalOpen = false;
                        } catch (err: any) {
                            state.error = err.message || 'Authentication failed. Please try again.';
                        } finally {
                            state.loading = false;
                            m.redraw();
                        }
                    }
                }, [
                    // Error Message
                    state.error && m('div', { class: 'p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-500 text-sm font-bold flex items-center gap-3 animate-shake' }, [
                        m('iconify-icon', { icon: 'lucide:alert-circle', class: 'text-xl shrink-0' }),
                        state.error
                    ]),

                    !state.isLogin && InputGroup('Full Name', 'lucide:user', 'text', 'John Doe', val => state.name = val),
                    InputGroup('Email Address', 'lucide:mail', 'text', 'john@example.com', val => state.email = val),
                    InputGroup('Password', 'lucide:lock', state.showPassword ? 'text' : 'password', '••••••••', val => state.password = val, 
                        m('button', {
                            type: 'button',
                            class: 'absolute end-4 top-1/2 -translate-y-1/2 w-8 h-8 !m-0 !p-1 rounded-lg flex items-center justify-center text-[var(--color-text-base)] opacity-40 hover:opacity-100 hover:bg-[var(--color-border)] transition-all',
                            onclick: () => { state.showPassword = !state.showPassword; }
                        }, m('iconify-icon', { icon: state.showPassword ? 'lucide:eye-off' : 'lucide:eye' }))
                    ),
                    !state.isLogin && InputGroup('Confirm Password', 'lucide:lock-keyhole', state.showPassword ? 'text' : 'password', '••••••••', val => state.confirmPassword = val),


                    // Terms Checkbox (Register Only)
                    !state.isLogin ? m('label', { class: 'flex items-start gap-3 mt-2 cursor-pointer group' }, [
                        m('div', { class: 'relative flex items-center justify-center shrink-0 w-5 h-5 mt-0.5' }, [
                            m('input', {
                                type: 'checkbox',
                                class: 'peer appearance-none w-5 h-5 border-2 border-[var(--color-border)] rounded hover:border-[var(--color-primary)] checked:bg-[var(--color-primary)] checked:border-[var(--color-primary)] transition-all cursor-pointer shadow-sm',
                                checked: state.agreeTerms,
                                onchange: (e: any) => { state.agreeTerms = e.target.checked; }
                            }),
                            m('iconify-icon', { icon: 'lucide:check', class: 'absolute text-white text-[10px] opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity' })
                        ]),
                        m('p', { class: 'text-xs font-medium opacity-70 group-hover:opacity-100 transition-opacity leading-relaxed flex-1' }, [
                            'I agree to the ',
                            m('a', { href: '#', class: 'text-[var(--color-primary)] hover:underline', onclick: (e: Event) => e.preventDefault() }, 'Terms of Service'),
                            ' and ',
                            m('a', { href: '#', class: 'text-[var(--color-primary)] hover:underline', onclick: (e: Event) => e.preventDefault() }, 'Privacy Policy'),
                            '.'
                        ])
                    ]) : null,

                    // Submit Button
                    m(Button, {
                        type: 'submit',
                        variant: 'primary',
                        size: 'lg',
                        class: 'w-full mt-8 tracking-wide',
                        loading: state.loading,
                        icon: state.isLogin ? 'lucide:arrow-right' : 'lucide:check-circle-2'
                    }, state.isLogin ? 'Sign In' : 'Create Account'),

                    // Toggle Link
                    m('div', { class: 'text-center mt-6 pt-6 border-t border-[var(--color-border)] opacity-80 hover:opacity-100 transition-opacity flex flex-col gap-2' }, [
                        m(Button, {
                            type: 'button',
                            variant: 'ghost',
                            class: 'text-sm w-full border border-transparent hover:border-[var(--color-border)] transition-all',
                            onclick: () => {
                                state.isLogin = !state.isLogin;
                                state.error = null;
                            }
                        }, state.isLogin ? "Don't have an account? Sign up" : "Already an member? Sign in")
                    ])
                ])
            ])
        ]);
    }
};

const state = {
    isLogin: true,
    loading: false,
    error: null as string | null,
    showPassword: false,
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeTerms: false
};
